import endpoints from "../../endpoints";
import account from "../../data/account";
import { call, softFail, isSoft, ApiError } from "../audiences/api";
export { softFail, isSoft, ApiError };

/*
 * Sales quotes, order desk and digests (SALES_QUOTES_ORDER_DESK.md §12,
 * SALES_DIGESTS.md §9). Kept apart from sales-api.js so the executive-view
 * work in another worktree never touches the same file.
 *
 * Every read here is FAIL-SOFT: a 404 / 405 / 501 resolves to null (the
 * backend is landing these routes in parallel from the same spec), and the
 * views show a calm "not available yet" line. Writes throw so a real
 * failure is visible, except where noted (tryWrite → null when missing).
 *
 * Ids travel as strings end to end (json-bigint parse in `call`).
 * Payloads are normalised defensively: camelCase or PascalCase, `items` or
 * `rows` or a bare array, numbers that arrive as strings.
 */

const ROOT = String(endpoints.product && endpoints.product.root || ""); // https://api.../v1
const Q = `${ROOT}/sales/quotes`;
const D = `${ROOT}/sales/digests`;
export const idStr = v => v === undefined || v === null ? "" : String(v);
const enc = v => encodeURIComponent(idStr(v));
const n = v => {
  if (v === undefined || v === null || v === "") return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const first = (o, ...keys) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};
// get a key in camelCase or PascalCase (or snake_case) without listing every spelling
export function g(o, key) {
  if (!o) return undefined;
  if (o[key] !== undefined && o[key] !== null) return o[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  if (o[pascal] !== undefined && o[pascal] !== null) return o[pascal];
  const snake = key.replace(/[A-Z]/g, c => "_" + c.toLowerCase());
  if (o[snake] !== undefined && o[snake] !== null) return o[snake];
  return undefined;
}
const items = d => {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  const x = first(d, "items", "Items", "rows", "Rows", "quotes", "orders", "tiers", "runs", "deliveries", "events");
  return Array.isArray(x) ? x : [];
};
const json = v => {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};
const tryWrite = p => softFail(p);
export const SHARE_ROOT = "https://share.plunk.io";
export const acceptanceLink = q => q && q.linkSlug ? `${SHARE_ROOT}/q/${q.linkSlug}` : "";

// ---- roles ----------------------------------------------------------------
export function isAdminUser() {
  const groups = ((account.get() || {}).groups || []).map(x => String(x).toLowerCase());
  return ["owner", "administrator", "root"].some(x => groups.includes(x));
}
export function isSalesUser() {
  const groups = ((account.get() || {}).groups || []).map(x => String(x).toLowerCase());
  return groups.includes("sales");
}
export const canViewQuotes = () => isAdminUser() || isSalesUser();
export const currentUserEmail = () => String((account.get() || {}).email || (account.get() || {}).username || "");

// ---- quotes: normalisers ----------------------------------------------------
export function normLine(r) {
  if (!r) return null;
  const cfg = json(g(r, "configJson")) || json(g(r, "config")) || {};
  const avail = json(g(r, "availabilityJson")) || json(g(r, "availability")) || null;
  const com = g(r, "comOrder") || null;
  return {
    id: idStr(g(r, "id")),
    position: n(g(r, "position")),
    productId: idStr(g(r, "productId")),
    sku: g(r, "sku") || "",
    title: g(r, "title") || "",
    imageUrl: g(r, "imageUrl") || g(r, "imageLink") || "",
    config: cfg && typeof cfg === "object" ? cfg : {},
    configText: g(r, "configText") || "",
    comOrderId: idStr(g(r, "comOrderId") || com && g(com, "id")),
    comStatus: g(r, "comStatus") || com && g(com, "status") || "",
    quantity: n(g(r, "quantity")) || 1,
    listPrice: n(g(r, "listPrice")),
    optionAdders: n(g(r, "optionAdders")) || 0,
    discountPct: n(g(r, "discountPct")) || 0,
    // discountRule = tier | category_override | override; discountSource = the tier code, the category name, or the overriding user
    discountRuleCode: String(g(r, "discountRule") || "").toLowerCase(),
    discountSource: g(r, "discountSource") || "",
    discountRule: ruleText(g(r, "discountRule"), g(r, "discountSource")),
    netPrice: n(g(r, "netPrice")),
    overridePrice: n(g(r, "overridePrice")),
    overrideReason: g(r, "overrideReason") || "",
    overrideBy: g(r, "overrideBy") || "",
    extended: n(g(r, "extended")),
    availability: avail && typeof avail === "object" ? avail : typeof avail === "string" ? {
      label: avail
    } : null,
    leadTimeDays: n(g(r, "leadTimeDays")),
    note: g(r, "note") || "",
    category: g(r, "productType") || g(r, "category") || ""
  };
}
function ruleText(rule, source) {
  const k = String(rule || "").toLowerCase();
  if (k === "category_override") return source ? `${source} override` : "category override";
  if (k === "override") return source ? `override · ${String(source).split("@")[0]}` : "override";
  if (k === "tier") return source ? `${source} tier` : "tier";
  return source || rule || "";
}
export function normQuote(r) {
  if (!r) return null;
  const customer = g(r, "customer") || null;
  const contact = json(g(r, "contactJson")) || json(g(r, "contact")) || {};
  const freight = json(g(r, "freightJson")) || json(g(r, "freight")) || null;
  const shipTo = json(g(r, "shipToJson")) || json(g(r, "shipTo")) || null;
  const accepted = json(g(r, "acceptedJson")) || json(g(r, "accepted")) || null;
  return {
    id: idStr(g(r, "id")),
    number: g(r, "number") || "",
    version: n(g(r, "version")) || 1,
    customerId: idStr(g(r, "customerId") || customer && g(customer, "id")),
    customerName: g(r, "customerName") || customer && g(customer, "name") || "",
    customerNumber: g(r, "customerNumber") || customer && g(customer, "customerNumber") || "",
    customerCity: g(r, "customerCity") || customer && (g(customer, "shipToCity") || g(customer, "city")) || "",
    customerRegion: g(r, "customerRegion") || customer && (g(customer, "shipToRegion") || g(customer, "region")) || "",
    contact: {
      name: g(contact, "name") || "",
      email: g(contact, "email") || "",
      phone: g(contact, "phone") || ""
    },
    repCode: g(r, "repCode") || "",
    repName: g(r, "repName") || "",
    owner: g(r, "owner") || "",
    title: g(r, "title") || "",
    status: String(g(r, "status") || "draft").toLowerCase(),
    currency: g(r, "currency") || "USD",
    priceTier: g(r, "priceTier") || g(r, "tier") || "",
    priceTierName: g(r, "priceTierName") || "",
    tierDiscountPct: n(g(r, "tierDiscountPct")),
    subtotalList: n(g(r, "subtotalList")) || 0,
    discountTotal: n(g(r, "discountTotal")) || 0,
    subtotalNet: n(g(r, "subtotalNet")) || n(g(r, "netTotal")) || 0,
    freight: freight && typeof freight === "object" ? {
      mode: g(freight, "mode") || "quote",
      amount: n(g(freight, "amount"))
    } : {
      mode: "quote",
      amount: null
    },
    terms: g(r, "terms") || "",
    notes: g(r, "notes") || "",
    shipTo: shipTo && typeof shipTo === "object" ? shipTo : null,
    validUntil: isoDate(g(r, "validUntil")),
    sentDate: g(r, "sentDate") || null,
    viewedDate: g(r, "viewedDate") || null,
    acceptedDate: g(r, "acceptedDate") || null,
    accepted: accepted && typeof accepted === "object" ? accepted : null,
    declinedDate: g(r, "declinedDate") || null,
    declinedReason: g(r, "declinedReason") || "",
    linkSlug: g(r, "linkSlug") || g(r, "slug") || "",
    link: g(r, "link") || "",
    pdfUrl: g(r, "pdfUrl") || "",
    orderRequestId: idStr(g(r, "orderRequestId")),
    orderRequestNumber: g(r, "orderRequestNumber") || "",
    orderStatus: g(r, "orderStatus") || "",
    lineCount: n(g(r, "lineCount")) || n(g(r, "lines")) || null,
    createdDate: g(r, "createdDate") || null,
    modifiedDate: g(r, "modifiedDate") || null,
    lines: items(g(r, "lines")).map(normLine).filter(Boolean),
    activity: items(g(r, "activity")).map(a => ({
      date: g(a, "date") || g(a, "createdDate") || "",
      action: g(a, "action") || g(a, "event") || "",
      actor: g(a, "actor") || g(a, "by") || "",
      note: g(a, "note") || ""
    }))
  };
}
export function isoDate(v) {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// The detail read answers { quote, lines[], activity[], orderRequest? }; a bare quote row is tolerated.
function unwrapQuote(d) {
  if (!d) return null;
  const qd = g(d, "quote") || d;
  const q = normQuote(qd);
  if (!q) return null;
  const lines = items(g(d, "lines"));
  if (lines.length && !q.lines.length) q.lines = lines.map(normLine).filter(Boolean);
  const act = items(g(d, "activity"));
  if (act.length && !q.activity.length) q.activity = act.map(a => ({
    date: g(a, "date") || g(a, "createdDate") || "",
    action: g(a, "action") || g(a, "event") || "",
    actor: g(a, "actor") || g(a, "by") || "",
    note: g(a, "note") || ""
  }));
  const or = g(d, "orderRequest");
  q.orderRequest = or ? normOrderRequest(or) : null;
  const totals = g(d, "totals");
  if (totals) applyTotals(q, totals);
  return q;
}
function applyTotals(q, t) {
  if (!t) return q;
  if (n(g(t, "subtotalList")) != null) q.subtotalList = n(g(t, "subtotalList"));
  if (n(g(t, "discountTotal")) != null) q.discountTotal = n(g(t, "discountTotal"));
  if (n(g(t, "subtotalNet")) != null) q.subtotalNet = n(g(t, "subtotalNet"));
  return q;
}

// ---- quotes: reads ----------------------------------------------------------
export async function listQuotes(query) {
  const d = await softFail(call(Q, {
    query: {
      ...(query || {}),
      $top: query && query.$top || 200
    }
  }));
  if (d === null) return null;
  return items(d).map(normQuote).filter(Boolean);
}
export async function getQuote(id) {
  const d = await softFail(call(`${Q}/${enc(id)}`));
  return d === null ? null : unwrapQuote(d);
}
export async function getQuotesSummary(range) {
  const d = await softFail(call(`${Q}/summary/${enc(range || "this_quarter")}`));
  if (d === null) return null;
  return {
    openQuotes: n(g(d, "openQuotes")) || n(g(d, "open")) || 0,
    openValue: n(g(d, "openValue")) || n(g(d, "openNet")) || 0,
    acceptanceRate: n(g(d, "acceptanceRate")),
    medianDaysToAccept: n(g(d, "medianDaysToAccept")),
    pendingErp: n(g(d, "pendingErp")) || n(g(d, "requestsPendingErp")) || 0,
    sent: n(g(d, "sent")),
    accepted: n(g(d, "accepted"))
  };
}

// ---- quotes: writes ---------------------------------------------------------
export async function createQuote(body) {
  const d = await tryWrite(call(Q, {
    method: "POST",
    body
  }));
  return d === null ? null : unwrapQuote(d);
}
export async function updateQuote(id, body) {
  const d = await call(`${Q}/${enc(id)}`, {
    method: "PUT",
    body
  });
  return unwrapQuote(d);
}
// Full ordered list; the answer carries the priced lines and totals.
export async function putLines(id, lines) {
  const body = {
    lines: lines.map((l, i) => ({
      id: l.id || undefined,
      position: i + 1,
      productId: idStr(l.productId),
      configJson: l.config && Object.keys(l.config).length ? l.config : undefined,
      quantity: Number(l.quantity) || 1,
      overridePrice: l.overridePrice != null && l.overridePrice !== "" ? Number(l.overridePrice) : undefined,
      overrideReason: l.overrideReason || undefined,
      note: l.note || undefined
    }))
  };
  const d = await call(`${Q}/${enc(id)}/lines`, {
    method: "PUT",
    body
  });
  return {
    lines: items(g(d, "lines") || d).map(normLine).filter(Boolean),
    totals: g(d, "totals") || null
  };
}
export async function createComcol(id, lineId) {
  const d = await call(`${Q}/${enc(id)}/lines/${enc(lineId)}/comcol`, {
    method: "POST"
  });
  return {
    comOrderId: idStr(g(d, "comOrderId")),
    status: g(d, "status") || "draft"
  };
}
export const requestPdf = id => call(`${Q}/${enc(id)}/pdf`, {
  method: "POST"
});
// → { quote, link, lines, totals, email: { sent, error }, pdfJobId }
export async function sendQuote(id, body) {
  const d = await call(`${Q}/${enc(id)}/send`, {
    method: "POST",
    body
  });
  const q = unwrapQuote(d);
  const link = g(d, "link") || acceptanceLink(q);
  if (q && link && !q.link) q.link = link;
  const em = g(d, "email") || {};
  return {
    quote: q,
    link,
    email: {
      sent: g(em, "sent") !== false && !g(em, "error"),
      error: g(em, "error") || ""
    },
    pdfJobId: idStr(g(d, "pdfJobId"))
  };
}
export async function approveQuote(id, note) {
  return unwrapQuote(await call(`${Q}/${enc(id)}/approve`, {
    method: "POST",
    body: {
      note: note || undefined
    }
  }));
}
export async function extendQuote(id, validUntil) {
  return unwrapQuote(await call(`${Q}/${enc(id)}/extend`, {
    method: "POST",
    body: {
      validUntil
    }
  }));
}
export async function duplicateQuote(id, customerId) {
  return unwrapQuote(await call(`${Q}/${enc(id)}/duplicate`, {
    method: "POST",
    body: {
      customerId: customerId || undefined
    }
  }));
}
export const voidQuote = (id, reason) => call(`${Q}/${enc(id)}/void`, {
  method: "POST",
  body: {
    reason
  }
});
export async function convertQuote(id, body) {
  const d = await call(`${Q}/${enc(id)}/convert`, {
    method: "POST",
    body: body || {}
  });
  return normOrderRequest(g(d, "orderRequest") || d);
}

// ---- order requests ---------------------------------------------------------
export const ORDER_STATUSES = [["pending_erp", "Pending ERP", "warning"], ["received", "Received", "info"], ["acknowledged", "Acknowledged", "info"], ["in_production", "In production", "info"], ["partially_shipped", "Partially shipped", "neutral"], ["shipped", "Shipped", "success"], ["invoiced", "Invoiced", "success"], ["on_hold", "On hold", "danger"], ["cancelled", "Cancelled", "neutral"]];
export const orderStatusLabel = s => {
  const k = String(s || "").toLowerCase();
  const hit = ORDER_STATUSES.find(([x]) => x === k);
  return hit ? hit[1] : s ? String(s).replace(/_/g, " ") : "";
};
export const orderStatusTone = s => {
  const hit = ORDER_STATUSES.find(([x]) => x === String(s || "").toLowerCase());
  return hit ? hit[2] : "neutral";
};
export function normOrderRequest(r) {
  if (!r) return null;
  const customer = g(r, "customer") || null;
  const shipTo = json(g(r, "shipToJson")) || json(g(r, "shipTo")) || null;
  const lines = json(g(r, "linesJson")) || g(r, "lines") || [];
  return {
    id: idStr(g(r, "id")),
    number: g(r, "number") || "",
    quoteId: idStr(g(r, "quoteId")),
    quoteNumber: g(r, "quoteNumber") || "",
    quoteVersion: n(g(r, "quoteVersion")),
    source: g(r, "source") || "quote",
    customerId: idStr(g(r, "customerId") || customer && g(customer, "id")),
    customerName: g(r, "customerName") || customer && g(customer, "name") || "",
    repCode: g(r, "repCode") || "",
    poNumber: g(r, "poNumber") || "",
    requestedDate: isoDate(g(r, "requestedDate")),
    shipTo: shipTo && typeof shipTo === "object" ? shipTo : null,
    lines: (Array.isArray(lines) ? lines : []).map(normLine).filter(Boolean),
    totalNet: n(g(r, "totalNet")) || n(g(r, "netTotal")) || 0,
    erpOrderId: g(r, "erpOrderId") || "",
    status: String(g(r, "status") || "pending_erp").toLowerCase(),
    statusDate: g(r, "statusDate") || null,
    statusNote: g(r, "statusNote") || "",
    acceptedBy: g(r, "acceptedBy") || "",
    createdBy: g(r, "createdBy") || "",
    createdDate: g(r, "createdDate") || null,
    modifiedDate: g(r, "modifiedDate") || null,
    events: items(g(r, "events")).map(normStatusEvent)
  };
}
export function normStatusEvent(e) {
  const lines = json(g(e, "linesJson")) || g(e, "lines") || [];
  const ships = json(g(e, "shipmentsJson")) || g(e, "shipments") || [];
  return {
    id: idStr(g(e, "id")),
    erpOrderId: g(e, "erpOrderId") || "",
    status: String(g(e, "status") || "").toLowerCase(),
    statusDate: g(e, "statusDate") || g(e, "receivedDate") || "",
    lines: Array.isArray(lines) ? lines.map(l => ({
      productId: idStr(g(l, "productId")),
      ordered: n(g(l, "ordered")),
      shipped: n(g(l, "shipped")),
      backordered: n(g(l, "backordered")),
      eta: isoDate(g(l, "eta"))
    })) : [],
    shipments: Array.isArray(ships) ? ships.map(s => ({
      carrier: g(s, "carrier") || "",
      tracking: g(s, "tracking") || "",
      shippedDate: isoDate(g(s, "shippedDate")),
      lines: Array.isArray(g(s, "lines")) ? g(s, "lines") : []
    })) : [],
    note: g(e, "note") || "",
    source: g(e, "source") || "api"
  };
}
export async function listOrders(query) {
  const d = await softFail(call(`${Q}/orders`, {
    query: {
      ...(query || {}),
      $top: query && query.$top || 200
    }
  }));
  if (d === null) return null;
  return items(d).map(normOrderRequest).filter(Boolean);
}
export async function getOrder(id) {
  const d = await softFail(call(`${Q}/orders/${enc(id)}`));
  if (d === null) return null;
  const or = normOrderRequest(g(d, "orderRequest") || d);
  const ev = items(g(d, "events"));
  if (or && ev.length && !or.events.length) or.events = ev.map(normStatusEvent);
  return or;
}
export const cancelOrder = (id, reason) => call(`${Q}/orders/${enc(id)}/cancel`, {
  method: "POST",
  body: {
    reason
  }
});

// ---- tiers and settings -------------------------------------------------------
export function normTier(t) {
  const ov = json(g(t, "categoryOverridesJson")) || json(g(t, "categoryOverrides")) || {};
  return {
    code: g(t, "code") || "",
    name: g(t, "name") || "",
    discountPct: n(g(t, "discountPct")) || 0,
    categoryOverrides: ov && typeof ov === "object" && !Array.isArray(ov) ? Object.fromEntries(Object.entries(ov).map(([k, v]) => [k, n(v) || 0])) : {},
    isDefault: !!g(t, "isDefault"),
    dealers: n(g(t, "dealers")) || n(g(t, "dealerCount")),
    modifiedBy: g(t, "modifiedBy") || "",
    modifiedDate: g(t, "modifiedDate") || null
  };
}
export async function getTiers() {
  const d = await softFail(call(`${Q}/tiers`));
  if (d === null) return null;
  return items(d).map(normTier);
}
export const putTiers = tiers => call(`${Q}/tiers`, {
  method: "PUT",
  admin: true,
  body: {
    tiers: tiers.map(t => ({
      code: t.code,
      name: t.name,
      discountPct: Number(t.discountPct) || 0,
      categoryOverrides: t.categoryOverrides || {},
      isDefault: !!t.isDefault
    }))
  }
});
export const DEFAULT_SETTINGS = {
  validDays: 30,
  maxOverridePct: 10,
  showListPrices: true,
  requireContactEmail: false,
  freightMode: "quote",
  terms: "",
  statusEmailsToDealer: false
};
export async function getQuoteSettings() {
  const d = await softFail(call(`${Q}/settings`));
  if (d === null) return null;
  return {
    validDays: n(g(d, "validDays")) != null ? n(g(d, "validDays")) : 30,
    maxOverridePct: n(g(d, "maxOverridePct")) != null ? n(g(d, "maxOverridePct")) : 10,
    showListPrices: g(d, "showListPrices") !== undefined ? !!g(d, "showListPrices") : true,
    requireContactEmail: !!g(d, "requireContactEmail"),
    freightMode: g(d, "freightMode") || "quote",
    terms: g(d, "terms") || "",
    statusEmailsToDealer: !!g(d, "statusEmailsToDealer")
  };
}
export const putQuoteSettings = s => call(`${Q}/settings`, {
  method: "PUT",
  admin: true,
  body: s
});

// ---- the local pricing rule (spec §6), used for the optimistic read ---------------
// list = sale_price when non-zero else price; adders from the rail; discount from
// the tier's category override when the product_type leaf matches, else the
// tier's pct; net = (list + adders) × (1 - pct); override replaces net; extended = unit × qty.
export function tierPctFor(tier, category) {
  if (!tier) return {
    pct: 0,
    rule: "no tier"
  };
  const leaf = String(category || "").split(/\s*[>/]\s*/).pop().trim().toLowerCase();
  const ov = tier.categoryOverrides || {};
  const hit = Object.keys(ov).find(k => k.trim().toLowerCase() === leaf);
  if (leaf && hit) return {
    pct: Number(ov[hit]) || 0,
    rule: `${hit} override`
  };
  return {
    pct: Number(tier.discountPct) || 0,
    rule: `${tier.name || tier.code} tier`
  };
}
export function priceLine(line, tier) {
  const list = Number(line.listPrice) || 0;
  const adders = Number(line.optionAdders) || 0;
  const {
    pct,
    rule
  } = line.discountPct != null && line._serverPriced ? {
    pct: line.discountPct,
    rule: line.discountRule || "tier"
  } : tierPctFor(tier, line.category);
  if (!line._serverPriced && line.overridePrice != null && line.overridePrice !== "") {/* the rule text stays the tier's; the override chip carries the reason and user */}
  const net = Math.round((list + adders) * (1 - pct / 100) * 100) / 100;
  const unit = line.overridePrice != null && line.overridePrice !== "" ? Number(line.overridePrice) : net;
  const qty = Number(line.quantity) || 1;
  return {
    ...line,
    discountPct: pct,
    discountRule: line._serverPriced ? line.discountRule || rule : rule,
    netPrice: net,
    unit,
    extended: Math.round(unit * qty * 100) / 100
  };
}
export function totalsOf(lines) {
  const t = {
    subtotalList: 0,
    discountTotal: 0,
    subtotalNet: 0,
    units: 0
  };
  lines.forEach(l => {
    const qty = Number(l.quantity) || 1;
    const list = (Number(l.listPrice) || 0) + (Number(l.optionAdders) || 0);
    t.subtotalList += list * qty;
    t.subtotalNet += Number(l.extended) || 0;
    t.units += qty;
  });
  t.discountTotal = Math.round((t.subtotalList - t.subtotalNet) * 100) / 100;
  t.subtotalList = Math.round(t.subtotalList * 100) / 100;
  t.subtotalNet = Math.round(t.subtotalNet * 100) / 100;
  return t;
}

// ---- catalog, configurations, dealers, reps (existing rails) ---------------------
// Catalog search = the Explorer / federated search read over `product`
// (title contains, id prefix). Rows: id, title, price, salePrice, imageLink, availability, productType, mpn.
export async function searchCatalog(term, top = 20) {
  const t = String(term || "").trim().toLowerCase();
  if (!t) return [];
  const where = "Title.ToLower().Contains(@0)||Id.ToLower().StartsWith(@0)";
  const d = await softFail(call(`${ROOT}/product`, {
    query: {
      $where: where,
      $params: t,
      $top: top
    }
  }));
  if (d === null) return null;
  return items(d).map(p => ({
    productId: idStr(g(p, "id") || g(p, "productId") || g(p, "sku")),
    sku: g(p, "sku") || g(p, "mpn") || idStr(g(p, "id")),
    title: g(p, "title") || "",
    imageUrl: g(p, "imageLink") || g(p, "imageUrl") || "",
    price: n(g(p, "price")),
    salePrice: n(g(p, "salePrice")),
    listPrice: (n(g(p, "salePrice")) || 0) > 0 ? n(g(p, "salePrice")) : n(g(p, "price")),
    availability: g(p, "availability") || "",
    category: g(p, "productType") || g(p, "googleProductCategory") || "",
    configurable: !!g(p, "configurable")
  }));
}
// Option sets from the configuration rail: GET /v1/product/configurations/{id}/options
// → [{ name, options: [{ value, label, adder, sku? }] }] (several spellings tolerated).
export async function getConfigOptions(productId) {
  const d = await softFail(call(`${ROOT}/product/configurations/${enc(productId)}/options`));
  if (d === null) return null;
  const sets = Array.isArray(d) ? d : g(d, "options") || g(d, "optionSets") || g(d, "components") || [];
  return (Array.isArray(sets) ? sets : Object.entries(sets).map(([name, options]) => ({
    name,
    options
  }))).map(s => ({
    name: g(s, "name") || g(s, "component") || g(s, "kind") || "",
    required: !!g(s, "required"),
    options: items(g(s, "options") || g(s, "values") || g(s, "choices") || s).map(o => typeof o === "string" ? {
      value: o,
      label: o,
      adder: 0
    } : {
      value: idStr(g(o, "value") || g(o, "code") || g(o, "name")),
      label: g(o, "label") || g(o, "name") || idStr(g(o, "value")),
      adder: n(g(o, "adder") || g(o, "price")) || 0,
      sku: g(o, "sku") || ""
    })
  })).filter(s => s.name);
}
// Dealer picker: the unified customer book (lifecycle) first, the wholesale roster as the fallback.
export async function searchDealers(term, top = 20) {
  const q = String(term || "").trim();
  const d = await softFail(call(`${ROOT}/lifecycle/customers`, {
    query: {
      q: q || undefined,
      top,
      channel: "b2b"
    }
  }));
  if (d !== null) {
    const rows = items(d).map(r => ({
      id: idStr(g(r, "id")),
      name: g(r, "name") || "",
      customerNumber: g(r, "customerNumber") || "",
      city: g(r, "shipToCity") || g(r, "city") || "",
      region: g(r, "shipToRegion") || g(r, "region") || "",
      email: g(r, "email") || "",
      phone: g(r, "phone") || "",
      assignedRepId: g(r, "assignedRepId") || "",
      priceTier: g(r, "priceTier") || "",
      lapseRisk: g(r, "lapseRisk") || "",
      stage: g(r, "lifecycleStage") || "",
      customerSince: g(r, "firstOrderDate") || g(r, "createdDate") || "",
      shipTo: {
        name: g(r, "name") || "",
        address: g(r, "shipToAddress") || g(r, "shipToStreet") || "",
        city: g(r, "shipToCity") || "",
        region: g(r, "shipToRegion") || "",
        postal: g(r, "shipToPostalCode") || ""
      }
    }));
    if (rows.length || !q) return rows;
  }
  const s = await softFail(call(`${ROOT}/sales/customers/this_year`, {
    query: {
      top: 200
    }
  }));
  if (s === null) return d === null ? null : [];
  const t = q.toLowerCase();
  return items(s).map(r => ({
    id: idStr(g(r, "customerId") || g(r, "id") || g(r, "customerNumber")),
    name: g(r, "name") || g(r, "customerName") || "",
    customerNumber: g(r, "customerNumber") || idStr(g(r, "customerId")),
    city: g(r, "shipToCity") || g(r, "city") || "",
    region: g(r, "shipToRegion") || g(r, "region") || "",
    email: "",
    phone: "",
    assignedRepId: g(r, "assignedRepId") || g(r, "repCode") || "",
    priceTier: g(r, "priceTier") || "",
    shipTo: {
      name: g(r, "name") || "",
      address: "",
      city: g(r, "shipToCity") || "",
      region: g(r, "shipToRegion") || "",
      postal: g(r, "shipToPostalCode") || ""
    }
  })).filter(r => !t || `${r.name} ${r.customerNumber}`.toLowerCase().includes(t)).slice(0, top);
}
// Assign a dealer's tier: `customer.price_tier` through the customer write model (contract assumption).
export const putDealerTier = (customerId, priceTier) => tryWrite(call(`${ROOT}/lifecycle/customers/${enc(customerId)}`, {
  method: "PUT",
  body: {
    priceTier
  }
}));
export async function listReps() {
  const d = await softFail(call(`${ROOT}/sales/reps/this_year`));
  if (d === null) return null;
  return items(d).map(r => ({
    repCode: idStr(g(r, "repCode") || g(r, "id")),
    name: g(r, "name") || idStr(g(r, "repCode")),
    email: g(r, "email") || "",
    status: g(r, "status") || ""
  })).filter(r => r.repCode);
}
export async function listStores() {
  const d = await softFail(call(`${ROOT}/store/performance/this_year`));
  if (d === null) return null;
  const rows = d && (d.stores || d.items || d.rows) || [];
  return (Array.isArray(rows) ? rows : []).map(r => ({
    storeId: idStr(g(r, "storeId") || g(r, "id")),
    name: g(r, "name") || idStr(g(r, "storeId")),
    managerEmail: g(r, "managerEmail") || "",
    manager: g(r, "manager") || ""
  })).filter(r => r.storeId);
}

// ---- digests (SALES_DIGESTS.md §9) --------------------------------------------------
export const DIGEST_KINDS = [["owner", "Owner digest", "the whole business · owners and sales leadership"], ["rep", "Rep digest", "each rep's book · one email per rep"], ["store", "Store digest", "per store · store managers · retail tenants"]];
export const DIGEST_SECTIONS = {
  owner: [["pace", "Plan"], ["reps", "Reps"], ["dealers", "Dealers"], ["coverage", "Coverage"], ["demand_gaps", "Demand gaps"], ["desk", "Orders desk"], ["marketing", "Marketing"]],
  rep: [["dealers", "Dealers to call"], ["reorder", "Reorder suggestions"], ["demand_gaps", "Demand gaps"], ["territory", "Territory"], ["desk", "Quotes and orders"]],
  store: [["categories", "Categories"], ["customers", "Customers"], ["reputation", "Reputation"], ["web", "Web to store"]]
};
export function defaultDigestSetting(kind, cadence) {
  const sections = {};
  (DIGEST_SECTIONS[kind] || []).forEach(([k]) => {
    sections[k] = true;
  });
  return {
    kind,
    cadence: cadence || "weekly",
    enabled: false,
    day: cadence === "monthly" ? 1 : 1,
    hourLocal: 7,
    timezone: "",
    periodEnd: "previous_sunday",
    recipients: kind === "rep" ? null : kind === "store" ? {} : [],
    replyTo: "",
    sections,
    basis: "booked",
    lastSentDate: null
  };
}
export function normDigestSetting(s) {
  const kind = String(g(s, "kind") || "owner").toLowerCase();
  const extra = {
    nextDueUtc: g(s, "nextDueUtc") || null,
    optedOut: n(g(s, "optedOut"))
  };
  const cadence = String(g(s, "cadence") || "weekly").toLowerCase();
  const base = defaultDigestSetting(kind, cadence);
  const rec = json(g(s, "recipientsJson"));
  const recipients = rec !== null && rec !== undefined ? rec : g(s, "recipients") !== undefined ? g(s, "recipients") : base.recipients;
  const sec = json(g(s, "sectionsJson")) || g(s, "sections") || null;
  return {
    ...base,
    enabled: !!g(s, "enabled"),
    day: n(g(s, "day")) != null ? n(g(s, "day")) : base.day,
    hourLocal: n(g(s, "hourLocal")) != null ? n(g(s, "hourLocal")) : 7,
    timezone: g(s, "timezone") || "",
    periodEnd: g(s, "periodEnd") || "previous_sunday",
    recipients: recipients === undefined ? base.recipients : recipients,
    replyTo: g(s, "replyTo") || "",
    sections: sec && typeof sec === "object" ? {
      ...base.sections,
      ...sec
    } : base.sections,
    basis: g(s, "basis") || "booked",
    lastSentDate: g(s, "lastSentDate") || null,
    modifiedBy: g(s, "modifiedBy") || "",
    ...extra
  };
}
// GET /v1/sales/digests/settings → { timezone, settings: [row], reps: { total, withoutEmail }, stores: [{ storeId, name, managerEmail }], canEdit }
// (older spellings and a bare array of rows are tolerated). Missing rows are filled with the spec defaults (off).
export async function getDigestSettings() {
  const d = await softFail(call(`${D}/settings`));
  if (d === null) return null;
  const rowsRaw = Array.isArray(d) ? d : g(d, "settings") || g(d, "digests") || g(d, "items") || [];
  const rows = (Array.isArray(rowsRaw) ? rowsRaw : []).map(normDigestSetting);
  const all = [];
  DIGEST_KINDS.forEach(([kind]) => ["weekly", "monthly"].forEach(cad => {
    const hit = rows.find(r => r.kind === kind && r.cadence === cad);
    all.push(hit || defaultDigestSetting(kind, cad));
  }));
  const optRaw = g(d, "optOuts") || g(d, "optouts");
  const reps = !Array.isArray(d) && g(d, "reps") || null;
  return {
    timezone: !Array.isArray(d) && g(d, "timezone") || "",
    settings: all,
    canEdit: Array.isArray(d) ? true : g(d, "canEdit") === undefined ? true : !!g(d, "canEdit"),
    repsWithoutEmail: reps && typeof reps === "object" ? n(g(reps, "withoutEmail")) ?? null : !Array.isArray(d) ? n(g(d, "repsWithoutEmail")) ?? null : null,
    repsTotal: reps && typeof reps === "object" ? n(g(reps, "total")) ?? null : !Array.isArray(d) ? n(g(d, "repsTotal")) ?? null : null,
    optOuts: Array.isArray(optRaw) ? optRaw.map(o => ({
      email: g(o, "email") || "",
      emailHash: g(o, "emailHash") || "",
      kind: g(o, "kind") || "all",
      createdDate: g(o, "createdDate") || ""
    })) : n(optRaw) != null ? n(optRaw) : null,
    stores: items(g(d, "stores")).map(r => ({
      storeId: idStr(g(r, "storeId") || g(r, "id")),
      name: g(r, "name") || "",
      managerEmail: g(r, "managerEmail") || ""
    }))
  };
}
export const putDigestSettings = (timezone, settings) => call(`${D}/settings`, {
  method: "PUT",
  admin: true,
  body: {
    timezone: timezone || undefined,
    settings: settings.map(s => ({
      kind: s.kind,
      cadence: s.cadence,
      enabled: !!s.enabled,
      day: Number(s.day) || 0,
      hourLocal: Number(s.hourLocal) || 7,
      timezone: s.timezone || undefined,
      periodEnd: s.periodEnd,
      recipients: s.recipients,
      replyTo: s.replyTo || null,
      sections: s.sections,
      basis: s.basis
    }))
  }
});
export function normRun(r) {
  const skipped = json(g(r, "skippedJson")) || g(r, "skipped") || [];
  return {
    id: idStr(g(r, "id")),
    kind: String(g(r, "kind") || "").toLowerCase(),
    cadence: String(g(r, "cadence") || "weekly").toLowerCase(),
    periodEnd: isoDate(g(r, "periodEnd")),
    occurrence: g(r, "occurrence") || "",
    status: String(g(r, "status") || "").toLowerCase(),
    recipients: n(g(r, "recipients")) || 0,
    sent: n(g(r, "sent")) || 0,
    bandChanges: n(g(r, "bandChanges")) || 0,
    skipped: Array.isArray(skipped) ? skipped.map(s => ({
      scope: idStr(g(s, "scope") || g(s, "scopeId")),
      reason: g(s, "reason") || ""
    })) : [],
    error: g(r, "error") || "",
    startedDate: g(r, "startedDate") || "",
    finishedDate: g(r, "finishedDate") || "",
    facts: json(g(r, "factsJson")) || g(r, "facts") || null,
    deliveries: items(g(r, "deliveries")).map(d => ({
      id: idStr(g(d, "id")),
      scopeKind: g(d, "scopeKind") || "",
      scopeId: idStr(g(d, "scopeId")),
      email: g(d, "email") || "",
      emailHash: g(d, "emailHash") || "",
      status: String(g(d, "status") || "").toLowerCase(),
      reason: g(d, "reason") || g(d, "skipReason") || "",
      createdDate: g(d, "createdDate") || ""
    }))
  };
}
export async function listDigestRuns(kind, top = 30) {
  const d = await softFail(call(`${D}/runs`, {
    query: {
      kind: kind || undefined,
      top
    }
  }));
  return d === null ? null : items(d).map(normRun);
}
export async function getDigestRun(id) {
  const d = await softFail(call(`${D}/runs/${enc(id)}`));
  if (d === null) return null;
  const run = normRun(g(d, "run") || d);
  const dl = items(g(d, "deliveries"));
  if (dl.length && !run.deliveries.length) run.deliveries = normRun({
    deliveries: dl
  }).deliveries;
  if (!run.facts && g(d, "facts")) run.facts = json(g(d, "facts")) || g(d, "facts");
  const sk = g(d, "skipped");
  if (Array.isArray(sk) && !run.skipped.length) run.skipped = sk.map(s => ({
    scope: idStr(g(s, "scope") || g(s, "scopeId")),
    reason: g(s, "reason") || ""
  }));
  return run;
}
// POST /v1/sales/digests/{kind}/send-now: { test: true, email, scope? } renders one email for the signed-in user;
// without `test` the run goes to the configured recipients now (the per-tenant run-now, DS 2026-10-05).
// test → { ok, test, runId, subject, scope, periodEnd, error, text, facts } synchronously; run → { ok, queued, eta }
export async function sendDigestNow(kind, body) {
  const d = await call(`${D}/${enc(kind)}/send-now`, {
    method: "POST",
    admin: true,
    body: body || {}
  });
  const o = d && typeof d === "object" ? d : {};
  return {
    ok: g(o, "ok") !== false,
    test: !!g(o, "test"),
    queued: !!g(o, "queued"),
    eta: g(o, "eta") || "",
    runId: idStr(g(o, "runId")),
    subject: g(o, "subject") || "",
    scope: idStr(g(o, "scope")),
    periodEnd: isoDate(g(o, "periodEnd")),
    error: g(o, "error") || "",
    text: g(o, "text") || "",
    facts: json(g(o, "facts")) || g(o, "facts") || null
  };
}
// Opt-outs: GET /v1/sales/digests/optouts → [{ email|emailHash|hash, kind, createdDate }] · POST { email, kind? } · DELETE { email | hash, kind? }
export async function listDigestOptOuts() {
  const d = await softFail(call(`${D}/optouts`));
  return d === null ? null : items(d).map(o => ({
    email: g(o, "email") || "",
    emailHash: g(o, "emailHash") || g(o, "hash") || "",
    kind: g(o, "kind") || "all",
    createdDate: g(o, "createdDate") || "",
    source: g(o, "source") || ""
  }));
}
export const addDigestOptOut = (email, kind) => call(`${D}/optouts`, {
  method: "POST",
  admin: true,
  body: {
    email,
    kind: kind && kind !== "all" ? kind : undefined
  }
});
export const removeDigestOptOut = o => tryWrite(call(`${D}/optouts`, {
  method: "DELETE",
  admin: true,
  body: {
    email: o.email || undefined,
    hash: !o.email ? o.emailHash || undefined : undefined,
    kind: o.kind && o.kind !== "all" ? o.kind : undefined
  }
}));